import { useTriggerTest } from "@/hooks/useTriggerTest";
import type { TriggerTestData } from "@/hooks/useTriggerTest";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FallbackDataFetcher, FallbackFetchContext } from "./types";

/** Normalize a trigger test response into an array of result items. */
export function normalizeTestResults(response: TriggerTestData | null): Record<string, unknown>[] {
  if (!response?.success) return [];
  if (response.results && response.results.length > 0) return response.results;
  if (response.result && Object.keys(response.result).length > 0) return [response.result];
  return [];
}
interface TriggerDataLoaderOptions {
  /** Fallback data fetcher — called when live test and last-run data return nothing */
  fetchFallbackData?: FallbackDataFetcher;
  /** Context needed by the fallback fetcher (wallets, connectorId, etc.) */
  fallbackContext?: Omit<FallbackFetchContext, "triggerType" | "triggerPayload">;
}
interface TriggerDataLoaderResult {
  results: Record<string, unknown>[];
  isLoading: boolean;
  error: string | null;
  source: "live" | "last_run" | "fallback" | "";
  reload: () => void;
}

/**
 * Shared hook for loading trigger test data with cascade:
 * 1. Try live test (if supported)
 * 2. Try last run data (if workflowId)
 * 3. Try fallback data fetcher (if configured) — e.g. Shopify orders, Polymarket trades
 *
 * Used by HistoricalPicker, EntitySearch, and ChatBotGuide to avoid duplicating fetch logic.
 */
export function useTriggerDataLoader(triggerType: string, triggerPayload: Record<string, unknown>, supportsLiveTest: boolean, workflowId?: string, options?: TriggerDataLoaderOptions): TriggerDataLoaderResult {
  const {
    fetchLiveData,
    fetchLastRunData
  } = useTriggerTest();
  const [results, setResults] = useState<Record<string, unknown>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"live" | "last_run" | "fallback" | "">("");
  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchFallbackData = options?.fetchFallbackData;
  const fallbackContext = options?.fallbackContext;
  const loadData = useCallback(async () => {
    // Cancel any in-flight request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsLoading(true);
    setError(null);

    // Preserve the most specific error from the cascade
    let latestError: string | null = null;
    try {
      if (supportsLiveTest) {
        const response = await fetchLiveData(triggerType, triggerPayload);
        if (controller.signal.aborted) return;
        const items = normalizeTestResults(response);
        if (items.length > 0) {
          setResults(items);
          setSource("live");
          return;
        }
        if (response?.error?.message) {
          latestError = response.error.message;
        }
      }
      if (workflowId) {
        const response = await fetchLastRunData(workflowId);
        if (controller.signal.aborted) return;
        const items = normalizeTestResults(response);
        if (items.length > 0) {
          setResults(items);
          setSource("last_run");
          return;
        }
      }

      // Step 3: Try the fallback data fetcher (e.g. Shopify orders, Polymarket trades)
      if (fetchFallbackData) {
        const items = await fetchFallbackData({
          triggerType,
          triggerPayload,
          wallets: fallbackContext?.wallets ?? [],
          connectorId: fallbackContext?.connectorId
        });
        if (controller.signal.aborted) return;
        if (items.length > 0) {
          setResults(items);
          setSource("fallback");
          return;
        }
      }
      setError(latestError ?? "No data found for this trigger configuration.");
    } catch (err) {
      if (!controller.signal.aborted) {
        setError(err instanceof Error ? err.message : "Failed to fetch trigger data. Please try again.");
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [fetchLiveData, fetchLastRunData, fetchFallbackData, fallbackContext, supportsLiveTest, triggerPayload, triggerType, workflowId]);

  // Auto-load on mount, abort on unmount
  useEffect(() => {
    loadData();
    return () => {
      abortControllerRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally load once on mount
  }, []);
  return {
    results,
    isLoading,
    error,
    source,
    reload: loadData
  };
}